import { Controller } from "@hotwired/stimulus"

// CONEK-3605 / GA programme F2.1 — broadcasts the entity the user is currently
// viewing so the companion (any renderer) can prime its context and forward it
// with every outgoing message.
//
// Usage — flat entity only (the original contract, unchanged):
//   <div data-controller="ai--companion-context"
//        data-ai--companion-context-entity-type-value="quote"
//        data-ai--companion-context-entity-id-value="<%= @quote.id %>"
//        data-ai--companion-context-entity-name-value="<%= @quote.visible_reference %>"></div>
//
// Usage — with the rich page-context envelope (workstream 1's page-context
// contract: `financial`, `related`, `canvas_state`, `cart`, and the
// drawing-editor branch's flat drawing_id/order_id/quote_name):
//   <div data-controller="ai--companion-context"
//        data-ai--companion-context-entity-type-value="Order"
//        data-ai--companion-context-entity-id-value="<%= @order.id %>"
//        data-ai--companion-context-entity-name-value="<%= @order.visible_reference %>"
//        data-ai--companion-context-context-value="<%= {financial: {...}}.to_json %>"></div>
//
// The server is already renderer-agnostic: AI::CompanionContext.normalize
// (packs/ai/app/services/ai/companion_context.rb) accepts BOTH the nested
// `{entity: {...}}` envelope React producers send and the flat shape Hotwire
// producers send, and passes every other key through untouched. So the only
// thing missing for parity was carrying the envelope on this event.
export default class extends Controller {
  static values = {
    entityType: String,
    entityId: String,
    entityName: String,
    // Optional rich envelope. Merged UNDER the three flat values below, so a
    // producer can never accidentally contradict the entity it declares.
    context: Object,
    // Optional page-specific suggestion chips: [{label, prompt}]. The
    // companion swaps its generic i18n chip row for these while this entity
    // is in context, and restores the generic set on disconnect.
    suggestions: Array,
  }

  connect() {
    const detail = {
      entityType: this.entityTypeValue,
      entityId: this.entityIdValue,
      entityName: this.entityNameValue,
    }
    // hasContextValue is false for an unset Object value, and an unset one
    // defaults to {} — check both so we never ship an empty envelope that
    // would look like "the producer supplied rich context, it was just blank".
    if (this.hasContextValue && Object.keys(this.contextValue).length > 0) {
      detail.context = this.contextValue
    }
    if (this.hasSuggestionsValue && this.suggestionsValue.length > 0) {
      detail.suggestedActions = this.suggestionsValue
    }
    // Defer so sibling controllers (ai--companion) that attach the listener
    // in their own connect() are guaranteed to be ready, regardless of DOM
    // order. Hard reload of /support/tickets/:id was dropping the event when
    // the broadcaster lived inside <main> and the listener lived after it.
    queueMicrotask(() => {
      window.dispatchEvent(new CustomEvent("companion:context", { detail }))
    })
  }

  disconnect() {
    window.dispatchEvent(new CustomEvent("companion:context-cleared"))
  }
}
